import type { SelectNode } from '@workspace/db/extra'
import { type ReactNode, useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  isCrossStoreMigration,
  MANAGE_ROLES,
  type OnboardVendor,
  useCancelProvisioning,
  useDeleteDevice,
  useDispatchProvisioning,
  useGenerateIpSchema,
  useLocationNodes,
  useProvisioningState,
  useRegisterDevice,
  useReplaceDevice,
} from '../../../features/locations/use-managed-devices'
import { useIdOrCode } from '../../use-id-or-code'
import { ActionRow, Btn, ConfirmDialog, Dialog, Field, Inline, input, PageHeader, Select, Tag, th } from './controls'
import { mono, Note, Section, SkeletonRows, Status, serif } from './ui'

const VENDORS: [OnboardVendor, string][] = [
  ['meraki', 'Meraki'],
  ['cloudflare', 'Cloudflare'],
  ['extreme', 'Extreme'],
  ['cradlepoint', 'Cradlepoint'],
  ['supermicro', 'Supermicro'],
]
const MERAKI_TYPES: [string, string][] = [
  ['edge', 'Edge (security appliance)'],
  ['switch', 'Switch'],
  ['ap', 'Access point'],
]
const MERAKI_NETWORK_TYPE: Record<string, string> = { edge: 'EDGE', switch: 'SWITCH', ap: 'AP' }

const phaseTone = (s: string) => (s === 'complete' ? 'ok' : s === 'running' || s === 'ready' ? 'warn' : 'off')

type Ctx = { idOrCode: string; storeCode: string; orgSlug: string; locationId: string }

function RegisterDialog({ ctx, onClose }: { ctx: Ctx; onClose: () => void }) {
  const register = useRegisterDevice()
  const [vendor, setVendor] = useState<OnboardVendor>('meraki')
  const [serial, setSerial] = useState('')
  const [deviceType, setDeviceType] = useState('edge')
  const [needMove, setNeedMove] = useState(false)
  const idLabel =
    vendor === 'cradlepoint' ? 'Cradlepoint ID' : vendor === 'supermicro' ? 'MAC address' : 'Serial number'
  const ok = serial.trim() !== '' && !register.isPending

  const submit = (confirmMove: boolean) =>
    register.mutate(
      {
        idOrCode: ctx.idOrCode,
        storeCode: ctx.storeCode,
        orgSlug: ctx.orgSlug,
        input: {
          vendor,
          serialNumber: serial,
          deviceType: vendor === 'extreme' || vendor === 'meraki' ? deviceType : undefined,
          networkType: vendor === 'meraki' ? MERAKI_NETWORK_TYPE[deviceType] : undefined,
        },
        confirmMove,
      },
      { onSuccess: onClose, onError: (e) => setNeedMove(isCrossStoreMigration(e)) },
    )

  return (
    <>
      <Dialog
        open={!needMove}
        eyebrow="New entry"
        title="Register a device"
        onClose={onClose}
        onSubmit={() => ok && submit(false)}
        footer={
          <>
            <Btn onClick={onClose} disabled={register.isPending}>
              Cancel
            </Btn>
            <Btn variant="primary" onClick={() => submit(false)} disabled={!ok}>
              {register.isPending ? 'Registering…' : 'Register'}
            </Btn>
          </>
        }
      >
        <Field label="Vendor">
          <Select
            value={vendor}
            onChange={(v) => {
              setVendor(v as OnboardVendor)
              setDeviceType(v === 'extreme' ? 'ap' : 'edge')
            }}
            options={VENDORS}
          />
        </Field>
        {vendor === 'meraki' ? (
          <Field label="Device type">
            <Select value={deviceType} onChange={setDeviceType} options={MERAKI_TYPES} />
          </Field>
        ) : null}
        {vendor === 'extreme' ? (
          <Field label="Device type">
            <Select
              value={deviceType === 'switch' ? 'switch' : 'ap'}
              onChange={setDeviceType}
              options={[
                ['ap', 'Access point'],
                ['switch', 'Switch'],
              ]}
            />
          </Field>
        ) : null}
        <Field label={idLabel} hint="Cmd+Enter to file">
          <input className={input} value={serial} onChange={(e) => setSerial(e.target.value)} style={mono} />
        </Field>
        {register.error && !needMove ? <Inline tone="bad">{apiErrorMessage(register.error)}</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={needMove}
        danger
        title="Move this device from another store?"
        body="It is on the books of a different store. Registering it here migrates it, and it stops serving the store it came from."
        confirmLabel="Move device"
        pending={register.isPending}
        error={register.error && !isCrossStoreMigration(register.error) ? apiErrorMessage(register.error) : null}
        onConfirm={() => submit(true)}
        onClose={() => setNeedMove(false)}
      />
    </>
  )
}

function ReplaceDialog({ ctx, node, onClose }: { ctx: Ctx; node: SelectNode; onClose: () => void }) {
  const replace = useReplaceDevice()
  const [serial, setSerial] = useState('')
  const [needMove, setNeedMove] = useState(false)
  const ok = serial.trim() !== '' && !replace.isPending

  const submit = (confirmMove: boolean) =>
    replace.mutate(
      {
        idOrCode: ctx.idOrCode,
        locationId: ctx.locationId,
        oldNodeSerial: node.serial ?? '',
        newNodeSerial: serial.trim(),
        nodeKind: node.kind,
        newManufacturer: node.manufacturer ?? undefined,
        extra: confirmMove ? { confirmMove: true } : undefined,
      },
      { onSuccess: onClose, onError: (e) => setNeedMove(isCrossStoreMigration(e)) },
    )

  return (
    <>
      <Dialog
        open={!needMove}
        eyebrow="Replacement"
        title={`Replace ${node.name ?? node.serial}`}
        onClose={onClose}
        onSubmit={() => ok && submit(false)}
        footer={
          <>
            <Btn onClick={onClose} disabled={replace.isPending}>
              Cancel
            </Btn>
            <Btn variant="primary" onClick={() => submit(false)} disabled={!ok}>
              {replace.isPending ? 'Replacing…' : 'Replace'}
            </Btn>
          </>
        }
      >
        <p className="text-[15px] italic leading-[1.5] text-[var(--color-muted)]" style={serif}>
          The new unit inherits the role and configuration of serial <span style={mono}>{node.serial}</span>.
        </p>
        <Field label="New serial number">
          <input className={input} value={serial} onChange={(e) => setSerial(e.target.value)} style={mono} />
        </Field>
        {replace.error && !needMove ? <Inline tone="bad">{apiErrorMessage(replace.error)}</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={needMove}
        danger
        title="Move the replacement from another store?"
        body="The replacement is on the books of a different store. Continuing migrates it here."
        confirmLabel="Move and replace"
        pending={replace.isPending}
        onConfirm={() => submit(true)}
        onClose={() => setNeedMove(false)}
      />
    </>
  )
}

export function LedgerManagedDevices() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const nodes = useLocationNodes(idOrCode)
  const prov = useProvisioningState(idOrCode)
  const genSchema = useGenerateIpSchema()
  const del = useDeleteDevice()
  const dispatch = useDispatchProvisioning()
  const cancel = useCancelProvisioning()

  const [registerOpen, setRegisterOpen] = useState(false)
  const [replaceNode, setReplaceNode] = useState<SelectNode | null>(null)
  const [deleteNode, setDeleteNode] = useState<SelectNode | null>(null)
  const [needOverride, setNeedOverride] = useState(false)

  const header = (aside?: ReactNode) => (
    <PageHeader
      idOrCode={idOrCode}
      section="Network"
      page="Managed devices"
      title="Managed devices"
      standfirst="The equipment on the books for this store, and how far its provisioning has come."
      aside={aside}
    />
  )

  if (location.isLoading || nodes.isLoading)
    return (
      <article>
        {header()}
        <SkeletonRows />
      </article>
    )
  const loc = location.data
  if (location.error || nodes.error || !loc)
    return (
      <article>
        {header()}
        <Note tone="error">
          The register could not be read: {(location.error ?? nodes.error)?.message ?? 'location not found.'}
        </Note>
      </article>
    )

  const ctx: Ctx = {
    idOrCode,
    storeCode: loc.aptosStoreCode ?? loc.code ?? idOrCode,
    orgSlug: loc.org?.slug ?? loc.banner ?? '',
    locationId: loc.id,
  }
  const list = nodes.data ?? []
  const state = prov.data
  const running = state?.jobs.some((j) => j.status === 'running') ?? false
  const phases = Object.entries(state?.phases ?? {}).filter(([, p]) => p.status !== 'not_applicable')

  const runDispatch = (override: boolean) =>
    dispatch.mutate(
      { idOrCode, locationId: ctx.locationId, override },
      {
        onSuccess: () => setNeedOverride(false),
        onError: (e) => setNeedOverride(!override && apiErrorMessage(e).toLowerCase().includes('override')),
      },
    )

  return (
    <article>
      {header(
        <Btn variant="primary" onClick={() => setRegisterOpen(true)}>
          Register a device
        </Btn>,
      )}

      <Section
        title="Provisioning"
        note={`Open to roles: ${MANAGE_ROLES.join(', ')}.`}
        aside={state ? <Tag tone={state.lifecycle === 'PROVISIONED' ? 'ok' : 'warn'}>{state.lifecycle}</Tag> : null}
      >
        {prov.isLoading ? (
          <Note>Reading provisioning state…</Note>
        ) : prov.error || !state ? (
          <Note>Provisioning state is unavailable.</Note>
        ) : (
          <>
            <ol className="m-0 list-none p-0">
              {phases.map(([name, p], i) => (
                <li key={name} className="flex items-baseline gap-4 border-b border-[var(--color-border)] py-2.5">
                  <span className="w-6 text-[13px] tabular-nums text-[var(--color-muted)]" style={serif}>
                    {i + 1}.
                  </span>
                  <span className="flex-1 text-[15px]" style={serif}>
                    {name.replace(/[_-]/g, ' ')}
                  </span>
                  <Tag tone={phaseTone(p.status)}>{p.status.replace('_', ' ')}</Tag>
                </li>
              ))}
            </ol>
            {state.jobs.length > 0 ? (
              <ul className="m-0 list-none p-0">
                {state.jobs.slice(0, 4).map((j) => (
                  <li
                    key={j.jobId}
                    className="flex items-baseline gap-4 border-b border-[var(--color-border)] py-2 text-[13.5px]"
                  >
                    <span className="min-w-0 flex-1 truncate italic" style={serif}>
                      {j.label}
                      {j.error ? <span className="ml-2 not-italic text-[var(--color-accent)]">{j.error}</span> : null}
                    </span>
                    <Status label={j.status === 'completed' ? 'up' : j.status === 'failed' ? 'down' : 'running'} />
                  </li>
                ))}
              </ul>
            ) : null}
            <ActionRow>
              <Btn
                disabled={genSchema.isPending}
                onClick={() => genSchema.mutate({ idOrCode, storeCode: ctx.storeCode, orgSlug: ctx.orgSlug })}
              >
                {genSchema.isPending ? 'Generating…' : 'Generate IP schema'}
              </Btn>
              <Btn variant="primary" onClick={() => runDispatch(false)} disabled={dispatch.isPending || running}>
                {dispatch.isPending ? 'Dispatching…' : 'Dispatch'}
              </Btn>
              <Btn
                variant="danger"
                disabled={cancel.isPending || !running}
                onClick={() => cancel.mutate({ idOrCode, locationId: ctx.locationId })}
              >
                {cancel.isPending ? 'Cancelling…' : 'Cancel dispatch'}
              </Btn>
            </ActionRow>
            <div className="grid gap-1" aria-live="polite">
              {genSchema.error ? <Inline tone="bad">{apiErrorMessage(genSchema.error)}</Inline> : null}
              {genSchema.isSuccess ? <Inline tone="ok">IP schema generated.</Inline> : null}
              {dispatch.error && !needOverride ? <Inline tone="bad">{apiErrorMessage(dispatch.error)}</Inline> : null}
              {cancel.error ? <Inline tone="bad">{apiErrorMessage(cancel.error)}</Inline> : null}
              {state.replacingSerial ? (
                <Inline tone="muted">A replacement is under way: {state.replacingSerial}.</Inline>
              ) : null}
            </div>
          </>
        )}
      </Section>

      <Section
        title="Devices"
        aside={<span className="tabular-nums text-[var(--color-muted)]">{list.length} on the books</span>}
      >
        {list.length === 0 ? (
          <Note>No devices are registered to this store.</Note>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] border-collapse" aria-label="Devices">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  {['Name', 'Kind', 'Model', 'Serial', 'Status', ''].map((h) => (
                    <th key={h || 'actions'} scope="col" className={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((n) => (
                  <tr key={n.serial ?? n.mac ?? n.name} className="h-11 border-b border-[var(--color-border)]">
                    <td className="pr-4 text-[15px] font-semibold" style={serif}>
                      {n.name ?? '—'}
                    </td>
                    <td className="pr-4 text-[13.5px] capitalize text-[var(--color-muted)]">
                      {String(n.kind).replace('_', ' ').toLowerCase()}
                    </td>
                    <td className="pr-4 text-[14px]">{n.model ?? '—'}</td>
                    <td className="pr-4 text-[13px]" style={mono}>
                      {n.serial ?? '—'}
                    </td>
                    <td className="pr-4">
                      <Status label={n.status ? 'Active' : 'Inactive'} />
                    </td>
                    <td className="text-right">
                      <span className="inline-flex gap-4">
                        <Btn onClick={() => setReplaceNode(n)}>Replace</Btn>
                        <Btn variant="danger" onClick={() => setDeleteNode(n)}>
                          Delete
                        </Btn>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {registerOpen ? <RegisterDialog ctx={ctx} onClose={() => setRegisterOpen(false)} /> : null}
      {replaceNode ? <ReplaceDialog ctx={ctx} node={replaceNode} onClose={() => setReplaceNode(null)} /> : null}
      <ConfirmDialog
        open={deleteNode !== null}
        danger
        title={`Strike ${deleteNode?.name ?? deleteNode?.serial ?? 'this device'} from the books?`}
        body="The device is removed from the store and released at the vendor. This cannot be undone."
        confirmLabel="Delete device"
        pending={del.isPending}
        error={del.error ? apiErrorMessage(del.error) : null}
        onConfirm={() =>
          deleteNode?.serial &&
          del.mutate(
            { idOrCode, locationId: ctx.locationId, deviceId: deleteNode.serial },
            { onSuccess: () => setDeleteNode(null) },
          )
        }
        onClose={() => {
          del.reset()
          setDeleteNode(null)
        }}
      />
      <ConfirmDialog
        open={needOverride}
        danger
        title="Dispatch with an override?"
        body="A provisioning precondition has not been met. Overriding dispatches anyway and may leave devices partly configured."
        confirmLabel="Override and dispatch"
        pending={dispatch.isPending}
        onConfirm={() => runDispatch(true)}
        onClose={() => setNeedOverride(false)}
      />
    </article>
  )
}
